import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';

// Count-up animation hook
const useCountUp = (end, duration = 2000, startOnMount = true) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const startTimeRef = useRef(null);

  // Parse the number, handling strings with commas, K, M suffixes
  const parseNumber = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const str = String(val).replace(/,/g, '');
    if (str.toLowerCase().endsWith('k')) return parseFloat(str) * 1000;
    if (str.toLowerCase().endsWith('m')) return parseFloat(str) * 1000000;
    return parseFloat(str) || 0;
  };

  const endNum = parseNumber(end);

  useEffect(() => {
    if (!startOnMount || endNum === 0) {
      setCount(endNum);
      return;
    }

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);

      // Easing function for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * endNum));

      if (progress < 1) {
        countRef.current = requestAnimationFrame(animate);
      } else {
        setCount(endNum);
      }
    };

    countRef.current = requestAnimationFrame(animate);

    return () => {
      if (countRef.current) cancelAnimationFrame(countRef.current);
    };
  }, [endNum, duration, startOnMount]);

  return count;
};

// Animated stat component
const AnimatedStat = ({ value, prefix = '', suffix = '', color, isAdminPreview }) => {
  const count = useCountUp(value, isAdminPreview ? 1500 : 2000);

  // Format with commas
  const formatted = count.toLocaleString();

  return (
    <span style={{ color }}>
      {prefix}{formatted}{suffix}
    </span>
  );
};

const StatsPreview = ({ isAdminPreview = true }) => {
  const [data, setData] = useState({ section: null, stats: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [sectionRes, statsRes] = await Promise.all([
        api.get('/business/stats/section/').catch(() => ({ data: {} })),
        api.get('/business/stats/'),
      ]);
      setData({
        section: sectionRes.data || {},
        stats: statsRes.data || []
      });
    } catch (err) {
      console.error('Error fetching stats preview:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (isAdminPreview) {
      const interval = setInterval(fetchData, 2000);
      return () => clearInterval(interval);
    }
  }, [isAdminPreview]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem', height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  if (!data.stats.length) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: 'rgba(255,255,255,0.5)',
        backgroundColor: '#1e293b',
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        No stats added yet. Add stats to see preview.
      </div>
    );
  }

  const bgColor = data.section?.background_color || '#1e293b';
  const statColor = data.section?.stat_color || '#ffffff';
  const layout = data.section?.layout_variant || 'grid';

  return (
    <div style={{
      padding: '2rem',
      backgroundColor: bgColor,
      minHeight: '200px'
    }}>
      {data.section?.title && (
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#fff', marginBottom: '0.25rem' }}>
            {data.section.title}
          </h2>
          {data.section?.subtitle && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
              {data.section.subtitle}
            </p>
          )}
        </div>
      )}

      <div style={{
        display: 'flex',
        flexDirection: layout === 'grid' ? 'row' : 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: layout === 'cards' ? '1.5rem' : layout === 'grid' ? '2rem 3rem' : '3rem',
        maxWidth: layout === 'grid' ? '600px' : '100%',
        margin: '0 auto'
      }}>
        {data.stats.map((stat, index) => (
          <div
            key={stat.id || index}
            style={{
              textAlign: 'center',
              padding: layout === 'cards' ? '1.5rem 2rem' : '1rem',
              backgroundColor: layout === 'cards' ? 'rgba(255,255,255,0.08)' : 'transparent',
              borderRadius: layout === 'cards' ? '1rem' : 0,
              border: layout === 'cards' ? '1px solid rgba(255,255,255,0.1)' : 'none',
              minWidth: layout === 'cards' ? '140px' : layout === 'grid' ? '120px' : 'auto',
              flex: layout === 'row' ? '1' : layout === 'grid' ? '0 0 calc(50% - 1.5rem)' : 'none',
              boxShadow: layout === 'cards' ? '0 4px 20px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            {stat.icon && (
              <span style={{ fontSize: '1.5rem', marginBottom: '0.5rem', display: 'block' }}>
                {stat.icon}
              </span>
            )}
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: statColor,
              marginBottom: '0.25rem'
            }}>
              <AnimatedStat
                value={stat.number || stat.value || 0}
                prefix={stat.prefix || ''}
                suffix={stat.suffix || ''}
                color={statColor}
                isAdminPreview={isAdminPreview}
              />
            </div>
            <div style={{
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {stat.label || stat.title}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsPreview;
